/** @format */

import React, { useEffect, useState } from "react";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	getAccountVerificationStatus,
	getAccountRequirement,
	fetchAllCirclesCurrentMonthContribution,
	fetchAllCirclesContributionHistory,
	fetchContributionsChartData,
} from "../../../redux/actions/circleAction";
import { getUser, getUserStats } from "../../../redux/actions/userActions";
import { selectAccountVerificationData } from "../../../redux/selectors/accountVerification";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import { AppDispatch } from "../../../redux/store/store";
import { Box, Grid } from "@mui/material";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import Main from "../../../utility/common/Main";
import { gridSpacing } from "../../../utility/generalUtils";
import { TotalUsers } from "../overview/TotalUsers";
import { TotalCircles } from "../overview/TotalCircles";
import { CirclesCurrentMonthContribution } from "../overview/CirclesCurrentMonthContribution";
import { CirclesTotalPayoutsForCurrentMonth } from "../overview/CirclesTotalPayoutsForCurrentMonth";
import { TotalContributionsHistory } from "../overview/TotalContributionsHistory";
import { TotalPayoutsHistory } from "../overview/TotalPayoutsHistory";
import {
	selectAllCircleStats,
	selectAllCirclesContributionHistory,
	selectContributionsChartData,
	selectIsLoading,
} from "../../../redux/selectors/circleSelector";
import ContributionsChart from "../chart/ContributionsChart";
import { SuccessNotification } from "../../allComponents/AllComponents";

const AdminDashboard = () => {
	const dispatch = useDispatch<AppDispatch>();
	const userData = useSelector(selectCurrentUser);
	const userAccountVerified = useSelector(selectAccountVerificationData);
	const circleStatsData = useSelector(selectAllCircleStats);
	const contributionHistoryData = useSelector(
		selectAllCirclesContributionHistory
	);
	const contributionsChartData = useSelector(selectContributionsChartData);
	const isLoadingCircleStats = useSelector(selectIsLoading);
	const [openNav, setOpenNav] = useState(false);
	const [isSyncing, setIsSyncing] = useState(false);
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error" | "info" | "warning",
	});
	const navigate = useNavigate();

	// Fetch all circles current month contribution and history on component mount
	useEffect(() => {
		dispatch(fetchAllCirclesCurrentMonthContribution());
		dispatch(fetchAllCirclesContributionHistory());
		dispatch(fetchContributionsChartData());
	}, [dispatch]);

	useEffect(() => {
		if (userData?._id) {
			dispatch(getUser(userData?._id));
			dispatch(getAccountVerificationStatus(userData?._id));
			dispatch(getUserStats(userData?._id));
		}
	}, [dispatch, userData?._id]);

	useEffect(() => {
		if (
			userData?.isVerified &&
			userAccountVerified?._id &&
			userData?.stripeAccountId
		) {
			dispatch(getAccountRequirement(userData?._id!)).unwrap();
		}

		if (
			userData?.isVerified &&
			userAccountVerified?._id &&
			!userData?.stripeAccountId
		) {
			return navigate(`/account-link/onboarding/refresh/${userData?._id}`, {
				replace: true,
			});
		}
	}, [
		userData?._id,
		dispatch,
		navigate,
		userData?.isVerified,
		userData?.stripeAccountId,
		userAccountVerified,
	]);

	const handleSyncChartData = async () => {
		setIsSyncing(true);
		try {
			await dispatch(fetchContributionsChartData()).unwrap();
			setNotification({
				open: true,
				message: "Chart successfully updated with latest data",
				severity: "success",
			});
		} catch (error: any) {
			setNotification({
				open: true,
				message: error || "Failed to sync chart data. Please try again later",
				severity: "error",
			});
		} finally {
			setIsSyncing(false);
		}
	};

	useEffect(() => {
		const interval = setInterval(() => {
			handleSyncChartData();
		}, 5 * 60 * 1000);

		return () => clearInterval(interval);
	});

	const handleCloseNotification = () => {
		setNotification((prev) => ({ ...prev, open: false }));
	};

	const chartSeries = React.useMemo(() => {
		if (!contributionsChartData) {
			return [
				{ name: "This month", data: [] },
				{ name: "Last month", data: [] },
			];
		}

		const chartData = contributionsChartData;

		return [
			{
				name: `${chartData.currentMonth.month} ${chartData.currentMonth.year}`,
				data: chartData.currentMonth.chartData,
			},
			{
				name: `${chartData.previousMonth.month} ${chartData.previousMonth.year}`,
				data: chartData.previousMonth.chartData,
			},
		];
	}, [contributionsChartData]);

	// Generate day categories based on current month's data length
	const chartCategories = React.useMemo(() => {
		if (!contributionsChartData?.currentMonth?.chartData) {
			return Array.from({ length: 31 }, (_, i) => i + 1);
		}
		const daysInMonth = contributionsChartData.currentMonth.chartData.length;
		return Array.from({ length: daysInMonth }, (_, i) => i + 1);
	}, [contributionsChartData]);

	return (
		<React.Fragment>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				<Main>
					{isLoadingCircleStats ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "60vh",
							}}>
							<LoadingSpinner loading={isLoadingCircleStats} size={45} />
						</Box>
					) : (
						<>
							<Grid container spacing={gridSpacing}>
								<Grid item xs={12}>
									<Grid container spacing={gridSpacing}>
										<Grid item lg={4} md={6} sm={6} xs={12}>
											<CirclesCurrentMonthContribution
												diff={12}
												trend="up"
												sx={{ height: "100%" }}
												value={`$${
													circleStatsData?.totalAmount?.toLocaleString() || "0"
												}`}
											/>
										</Grid>

										<Grid item lg={4} md={6} sm={6} xs={12}>
											<TotalUsers
												diff={12}
												trend="down"
												sx={{ height: "100%" }}
												value={circleStatsData?.totalUsers || 0}
											/>
										</Grid>

										<Grid item lg={4} md={6} sm={6} xs={12}>
											<TotalCircles
												value={circleStatsData?.totalCircles || 0}
												sx={{ height: "100%" }}
											/>
										</Grid>

										<Grid item lg={4} md={6} sm={6} xs={12}>
											<CirclesTotalPayoutsForCurrentMonth
												sx={{ height: "100%" }}
												value={circleStatsData?.totalPayoutAmount || 0}
											/>
										</Grid>

										<Grid item lg={4} md={6} sm={6} xs={12}>
											<TotalContributionsHistory
												sx={{ height: "100%" }}
												totalAmount={contributionHistoryData?.totalAmount || 0}
												totalContributions={
													contributionHistoryData?.totalContributions || 0
												}
											/>
										</Grid>

										<Grid item lg={4} md={6} sm={6} xs={12}>
											<TotalPayoutsHistory
												sx={{ height: "100%" }}
												totalPayoutAmount={
													contributionHistoryData?.totalPayoutAmount || 0
												}
												totalPayouts={
													contributionHistoryData?.totalPayouts || 0
												}
											/>
										</Grid>
									</Grid>
								</Grid>
							</Grid>

							<Grid container spacing={gridSpacing} sx={{ mt: 3 }}>
								<Grid item lg={12} md={12} sm={12} xs={12}>
									<ContributionsChart
										chartSeries={chartSeries}
										categories={chartCategories}
										sx={{ height: "100%" }}
										onSync={handleSyncChartData}
										isSync={isSyncing}
									/>
								</Grid>
							</Grid>
						</>
					)}
				</Main>
			</Box>

			<SuccessNotification
				open={notification.open}
				onClose={handleCloseNotification}
				severity={notification.severity}
				message={notification.message}
				autoHideDuration={5000}
			/>
		</React.Fragment>
	);
};

export default AdminDashboard;
